import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { TbQrcode, TbCreditCardPay, TbChevronDown } from "react-icons/tb";
import CardQrPopup from "src/components/CardQrPopup/CardQrPopup";
import CardReaderPopup from "src/components/CardReaderPopup/CardReaderPopup";
import "./cardPaymentButton.scss";

interface Props {
    invoiceId: string;
    readableId: string;
    /** Backend flags: QR at the desk (`cardDeskQr`) / card terminal (`cardTerminal`). */
    qr: boolean;
    terminal: boolean;
    onPaid: () => void;
    /** "page": labelled button on the invoice page; "row": icon button in the invoice list's actions. */
    variant: "page" | "row";
    className?: string;
    disabled?: boolean;
}

type Method = "qr" | "terminal";

/**
 * Card payment at the desk (SumUp, chapter 10) as one action: opens the QR or the card terminal
 * directly when only one is available, otherwise a small menu to pick.
 */
export default function CardPaymentButton({ invoiceId, readableId, qr, terminal, onPaid, variant, className, disabled }: Props) {
    const { t } = useTranslation();
    const [open, setOpen] = useState<Method | null>(null);
    const [menuAt, setMenuAt] = useState<{ top: number; right: number } | null>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const both = qr && terminal;

    useEffect(() => {
        if (!menuAt) return;
        const close = () => setMenuAt(null);
        const onDown = (e: MouseEvent) => {
            const target = e.target as Node;
            if (!menuRef.current?.contains(target) && !triggerRef.current?.contains(target)) close();
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { close(); triggerRef.current?.focus(); } };
        document.addEventListener("mousedown", onDown);
        document.addEventListener("keydown", onKey);
        window.addEventListener("scroll", close, true);
        window.addEventListener("resize", close);
        menuRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
        return () => {
            document.removeEventListener("mousedown", onDown);
            document.removeEventListener("keydown", onKey);
            window.removeEventListener("scroll", close, true);
            window.removeEventListener("resize", close);
        };
    }, [menuAt]);

    if (!qr && !terminal) return null;

    const onTrigger = () => {
        if (!both) { setOpen(qr ? "qr" : "terminal"); return; }
        if (menuAt) { setMenuAt(null); return; }
        const r = triggerRef.current!.getBoundingClientRect();
        setMenuAt({ top: r.bottom + 4, right: window.innerWidth - r.right });
    };
    const pick = (m: Method) => { setMenuAt(null); setOpen(m); };

    const label = both ? t("cardPay.button") : qr ? t("cardQr.button") : t("cardReader.button");
    const Icon = !both && qr ? TbQrcode : TbCreditCardPay;

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className={className}
                onClick={onTrigger}
                disabled={disabled}
                title={variant === "row" ? label : undefined}
                aria-label={variant === "row" ? label : undefined}
                aria-haspopup={both ? "menu" : undefined}
                aria-expanded={both ? !!menuAt : undefined}
            >
                <Icon />
                {variant === "page" && <> {label}</>}
                {variant === "page" && both && <TbChevronDown className="card-pay-trigger__chevron" />}
            </button>

            {menuAt && createPortal(
                <div ref={menuRef} className="card-pay-menu" role="menu" style={{ top: menuAt.top, right: menuAt.right }}>
                    <button type="button" role="menuitem" className="card-pay-menu__item" onClick={() => pick("qr")}>
                        <TbQrcode />
                        <span>
                            <strong>{t("cardPay.qr")}</strong>
                            <small>{t("cardPay.qrHint")}</small>
                        </span>
                    </button>
                    <button type="button" role="menuitem" className="card-pay-menu__item" onClick={() => pick("terminal")}>
                        <TbCreditCardPay />
                        <span>
                            <strong>{t("cardPay.terminal")}</strong>
                            <small>{t("cardPay.terminalHint")}</small>
                        </span>
                    </button>
                </div>,
                document.body,
            )}

            {open === "qr" && <CardQrPopup invoiceId={invoiceId} readableId={readableId} onClose={() => setOpen(null)} onPaid={onPaid} />}
            {open === "terminal" && <CardReaderPopup invoiceId={invoiceId} readableId={readableId} onClose={() => setOpen(null)} onPaid={onPaid} />}
        </>
    );
}
